import React from "react";
import {
  Shield,
  Lock,
  Eye,
  Trash2,
  Edit3,
  UserCheck,
  Settings,
  ShieldCheck,
  Share2,
  Globe,
  CreditCard,
  Key,
  AlertTriangle,
  Users,
  Search,
  CheckCircle,
  Database,
  FileText,
  Mail,
  AlertCircle,
  MousePointer2,
  ExternalLink,
  Cookie,
  Info,
  RefreshCw,
  ShieldAlert,
  UserMinus,
  Bell,
  XCircle,
  Calendar,
  CheckCircle2,
} from "lucide-react";

const PrivacyPolicy = () => {
  const currentDate = new Date().toLocaleDateString("en-IN", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  const highlights = [
    { icon: <Shield size={20} />, text: "No Data Selling" },
    { icon: <Lock size={20} />, text: "DPDP Act Compliant" },
    { icon: <Trash2 size={20} />, text: "Right to be Forgotten" },
  ];

  return (
    <div className="min-h-screen bg-[#FFF0F3] text-gray-800 font-sans selection:bg-rose-200">
      {/* Decorative Top Bar */}
      <div className="h-2 bg-rose-500 w-full" />

      <div className="max-w-7xl mx-auto px-6 py-16">
        {/* Header Section */}
        <header className="text-center mb-16">
          <div className="inline-block px-5 py-2 rounded-full bg-rose-100 text-rose-700 text-base font-extrabold mb-6 tracking-wider uppercase shadow-sm">
            Your Privacy • Our Promise
          </div>
          <h1 className="text-5xl md:text-6xl font-extrabold text-gray-900 mb-5 tracking-tight leading-tight">
            Privacy <span className="text-rose-500">Policy</span>
          </h1>
          <p className="text-xl text-gray-700 font-medium italic mb-6">
            Eveya – A brand of Farmfit Agrihealth LLP
          </p>
          <div className="flex justify-center items-center gap-6 text-base font-medium text-gray-600 flex-wrap">
            <span className="bg-white px-4 py-1.5 rounded-full border border-rose-200 shadow-sm">
              Last Updated: {currentDate}
            </span>
            <span className="text-rose-400 hidden sm:inline">•</span>
            <span className="bg-white px-4 py-1.5 rounded-full border border-rose-200 shadow-sm">
              Effective Immediately
            </span>
          </div>
        </header>

        {/* Highlights Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          {highlights.map((item, index) => (
            <div
              key={index}
              className="bg-white p-7 rounded-2xl border border-rose-100 shadow-xl shadow-rose-200/50 flex items-center gap-5 transition-all hover:scale-105 hover:shadow-rose-300/60"
            >
              <div className="p-4 bg-rose-500 rounded-xl text-white flex-shrink-0">
                {item.icon}
              </div>
              <span className="font-bold text-lg text-gray-800">
                {item.text}
              </span>
            </div>
          ))}
        </div>

        {/* Main Content Sections */}
        <div className="space-y-10">
          {/* 1. Introduction - Enhanced */}
          <Section
            title="1. Introduction & Our Commitment"
            icon={<Eye size={28} className="text-rose-500" />}
          >
            {/* 1.1 & 1.2: General Introduction & Commitment */}
            <p className="text-lg leading-relaxed text-gray-700 mb-6">
              At <strong className="text-rose-600">Eveya</strong>, your trust
              and comfort matter more than anything else. This Privacy Policy
              explains how we —{" "}
              <strong>Eveya, a brand of Farmfit Agrihealth LLP</strong>{" "}
              (hereinafter referred to as "Company," "Firm," "we," "us," or
              "our") — collect, use, disclose, and protect your personal
              information and health data.
            </p>

            <p className="text-lg leading-relaxed text-gray-700 mb-6">
              We are committed to ensuring transparency in how we handle your
              data, especially health-related information. Our mission starts
              with treating your sensitive data with the highest level of care,
              confidentiality, and respect.
            </p>

            {/* 1.1: Scope of Application */}
            <div className="bg-gradient-to-r from-rose-50 to-pink-50 p-6 rounded-2xl border border-rose-200 my-8">
              <p className="font-semibold text-rose-700 mb-3 text-lg">
                This policy applies when you:
              </p>
              <ul className="grid md:grid-cols-2 gap-3 text-gray-700">
                <li className="flex items-center gap-2">
                  <span className="text-rose-500 font-bold">•</span> Visit
                  www.eveya.in
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-rose-500 font-bold">•</span> Use our
                  mobile app
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-rose-500 font-bold">•</span> Make a
                  purchase
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-rose-500 font-bold">•</span> Create an
                  account
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-rose-500 font-bold">•</span> Subscribe
                  to our services
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-rose-500 font-bold">•</span> Participate
                  in Sponsorships
                </li>
              </ul>
            </div>

            {/* 1.3: Data Categories */}
            <div className="mb-8">
              <h3 className="text-xl font-semibold text-gray-800 mb-4">
                What Data This Policy Covers
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="p-4 bg-white border border-gray-100 rounded-xl shadow-sm">
                  <h4 className="font-bold text-rose-600 mb-2">
                    Personal Data
                  </h4>
                  <p className="text-gray-600 text-sm">
                    Name, email address, phone number, physical address, and
                    account credentials.
                  </p>
                </div>
                <div className="p-4 bg-white border border-gray-100 rounded-xl shadow-sm">
                  <h4 className="font-bold text-rose-600 mb-2">
                    Health-Related Data
                  </h4>
                  <p className="text-gray-600 text-sm">
                    Menstrual cycle information, product preferences, allergies,
                    and wellness goals.
                  </p>
                </div>
                <div className="p-4 bg-white border border-gray-100 rounded-xl shadow-sm">
                  <h4 className="font-bold text-rose-600 mb-2">
                    Payment Information
                  </h4>
                  <p className="text-gray-600 text-sm">
                    Processed securely through encrypted payment gateways to
                    ensure transaction safety.
                  </p>
                </div>
                <div className="p-4 bg-white border border-gray-100 rounded-xl shadow-sm">
                  <h4 className="font-bold text-rose-600 mb-2">Usage Data</h4>
                  <p className="text-gray-600 text-sm">
                    Pages visited, time spent on site, device information, and
                    browser details.
                  </p>
                </div>
              </div>
            </div>

            {/* 1.4: Policy Exclusions */}
            <div className="bg-gray-50 p-6 rounded-2xl border border-gray-200 mt-8">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">
                What This Policy Does NOT Cover
              </h3>
              <p className="text-gray-600 mb-4">
                Please note that this Privacy Policy does not apply to:
              </p>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-start gap-2">
                  <span className="text-gray-400 mt-1">•</span>
                  <span>
                    <strong>Third-party websites:</strong> Links on our site
                    leading to external websites.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-gray-400 mt-1">•</span>
                  <span>
                    <strong>Integrations:</strong> Third-party apps or services
                    integrated with our platform.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-gray-400 mt-1">•</span>
                  <span>
                    <strong>External Practices:</strong> Privacy practices of
                    other companies not owned by Farmfit Agrihealth LLP.
                  </span>
                </li>
              </ul>
              <p className="mt-4 text-sm text-gray-500 italic">
                When you visit third-party sites, we encourage you to read their
                specific privacy policies.
              </p>
            </div>

            <p className="text-gray-500 italic text-center mt-6 text-sm">
              This policy is designed to comply with the{" "}
              <strong>
                Digital Personal Data Protection Act, 2023 (DPDP Act)
              </strong>
              and prioritizes the protection of your sensitive personal data.
            </p>
          </Section>

          {/* 2. Information we collect */}
          <Section
            title="2. Information We Collect"
            icon={<UserCheck size={28} className="text-rose-500" />}
          >
            {/* 2.1 Information You Provide Directly */}
            <h3 className="font-bold text-xl text-gray-900 mt-2 mb-4 border-b border-rose-100 pb-2">
              2.1 Information You Provide Directly
            </h3>

            <div className="space-y-6">
              <div>
                <p className="font-semibold text-gray-800">
                  A. Account Registration:
                </p>
                <ul className="list-disc pl-6 space-y-1 mt-2 text-gray-700">
                  <li>Full name</li>
                  <li>Email address</li>
                  <li>Phone number</li>
                  <li>Date of birth (to verify age 18+)</li>
                  <li>Gender identity (optional; for personalization)</li>
                  <li>Password (encrypted and stored securely)</li>
                </ul>
              </div>

              <div>
                <p className="font-semibold text-gray-800">
                  B. Delivery & Billing Information:
                </p>
                <ul className="list-disc pl-6 space-y-1 mt-2 text-gray-700">
                  <li>Home address & Billing address (if different)</li>
                  <li>Delivery preferences</li>
                  <li>
                    Payment method (card details processed by secure payment
                    gateways;
                    <span className="italic">
                      {" "}
                      we do NOT store full card numbers
                    </span>
                    )
                  </li>
                </ul>
              </div>

              <div className="bg-rose-50 p-5 rounded-xl border-l-4 border-rose-500 shadow-sm">
                <p className="font-semibold text-rose-800">
                  C. Health & Preference Information:
                </p>
                <p className="text-xs font-bold text-rose-600 uppercase tracking-wider mt-1 mb-3">
                  [IMPORTANT]: Sensitive Personal Data under DPDP Act
                </p>
                <ul className="list-disc pl-6 space-y-1.5 text-gray-700">
                  <li>Menstrual cycle length and regularity (optional)</li>
                  <li>Product allergies or skin sensitivities</li>
                  <li>
                    Preferred product types (cramp reduction pads, period
                    panties, etc.)
                  </li>
                  <li>
                    Special requirements or medical conditions you choose to
                    disclose
                  </li>
                </ul>
                <p className="text-xs text-rose-700 mt-3 italic">
                  We handle this data with the highest levels of confidentiality
                  and encryption.
                </p>
              </div>

              <div>
                <p className="font-semibold text-gray-800">
                  D. Customer Service & Communication:
                </p>
                <ul className="list-disc pl-6 space-y-1 mt-2 text-gray-700">
                  <li>Emails, messages, and support chat conversations</li>
                  <li>Feedback, reviews, and survey responses</li>
                </ul>
              </div>
            </div>

            {/* 2.2 Information Collected Automatically */}
            <h3 className="font-bold text-xl text-gray-900 mt-10 mb-4 border-b border-rose-100 pb-2">
              2.2 Information Collected Automatically
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <p className="font-semibold text-gray-800 mb-2">
                  A. Website/App Usage:
                </p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Pages visited and time spent</li>
                  <li>• Products viewed and added to cart</li>
                  <li>• Clicks, interactions, and search queries</li>
                  <li>• Purchase history or abandoned carts</li>
                </ul>
              </div>
              <div>
                <p className="font-semibold text-gray-800 mb-2">
                  B. Device Information:
                </p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Device type (Phone, Laptop, Tablet)</li>
                  <li>• Operating system (iOS, Android, Windows)</li>
                  <li>• Browser type and IP address</li>
                  <li>• Approximate location (City-level from IP)</li>
                </ul>
              </div>
            </div>

            <div className="mt-6 p-4 bg-gray-50 rounded-xl border border-gray-200">
              <p className="font-semibold text-gray-800 mb-2">
                C. Cookies & Tracking:
              </p>
              <div className="grid md:grid-cols-2 gap-4 text-sm text-gray-600">
                <p>
                  <strong>Session Cookies:</strong> Temporary; deleted when you
                  close browser.
                </p>
                <p>
                  <strong>Persistent Cookies:</strong> Stored longer to remember
                  login/preferences.
                </p>
                <p>
                  <strong>Analytics Cookies:</strong> Used to track site
                  performance.
                </p>
                <p>
                  <strong>Marketing Cookies:</strong> Remember interests for
                  relevant ads.
                </p>
              </div>
              <p className="text-xs text-gray-500 mt-3 italic">
                Note: You can disable cookies in browser settings, but some
                features may not work properly.
              </p>
            </div>

            <div className="mt-4 text-sm text-gray-700">
              <p>
                <strong>D. Analytics Services:</strong> We use Google Analytics.
                They anonymize your IP so we cannot identify you individually.
                Please see Google’s privacy policy for details.
              </p>
            </div>

            {/* 2.3 Information from Third Parties */}
            <h3 className="font-bold text-xl text-gray-900 mt-10 mb-4 border-b border-rose-100 pb-2">
              2.3 Information from Third Parties
            </h3>
            <ul className="space-y-4 text-gray-700">
              <li className="flex gap-3">
                <span className="text-rose-500 font-bold">A.</span>
                <span>
                  <strong>Payment Gateways:</strong> Partners share confirmation
                  and transaction status; they do NOT share full card numbers.
                </span>
              </li>
              <li className="flex gap-3">
                <span className="text-rose-500 font-bold">B.</span>
                <span>
                  <strong>Delivery Partners:</strong> Couriers share delivery
                  updates and GPS package location for accurate tracking.
                </span>
              </li>
              <li className="flex gap-3">
                <span className="text-rose-500 font-bold">C.</span>
                <span>
                  <strong>Corporate/University Partners:</strong> May provide
                  data for wellness programs. Employees/students must separately
                  consent to our Terms.
                </span>
              </li>
              <li className="flex gap-3">
                <span className="text-rose-500 font-bold">D.</span>
                <span>
                  <strong>Social Media:</strong> If using Facebook/Google login,
                  we receive public profile info only. We do NOT access your
                  feed or private messages.
                </span>
              </li>
            </ul>

            {/* 2.4 Information NOT Collected */}
            <div className="mt-10 bg-gray-900 text-white p-6 rounded-2xl shadow-lg">
              <h3 className="font-bold text-xl mb-4 text-rose-400 border-b border-gray-700 pb-2">
                2.4 Information We Do NOT Collect
              </h3>
              <ul className="grid md:grid-cols-2 gap-3 text-sm">
                <li className="flex items-center gap-2">
                  <span className="text-rose-500">✕</span> Aadhaar, PAN, or
                  Government IDs
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-rose-500">✕</span> Biometric data
                  (Fingerprints/Iris)
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-rose-500">✕</span>{" "}
                  Camera/Microphone/Live GPS (unless authorized)
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-rose-500">✕</span> Cross-site tracking
                  (Third-party cookies)
                </li>
              </ul>
            </div>
          </Section>

          {/* 3. How we use your information */}
          <Section
            title="3. How We Use Your Information"
            icon={<Settings size={28} className="text-rose-500" />}
          >
            <div className="space-y-8">
              {/* 3.1 & 3.2: Operational & Personalization */}
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <h3 className="font-bold text-gray-900 border-b border-rose-100 pb-1">
                    3.1 Providing Services
                  </h3>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Process orders, deliveries, and subscriptions</li>
                    <li>• Manage billing and secure payments</li>
                    <li>• Send order confirmations and tracking updates</li>
                    <li>• Respond to customer service and support requests</li>
                    <li>• Handle refunds and transaction disputes</li>
                  </ul>
                </div>
                <div className="space-y-3">
                  <h3 className="font-bold text-gray-900 border-b border-rose-100 pb-1">
                    3.2 Personalization
                  </h3>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Product recommendations based on your unique data</li>
                    <li>• Customizing your overall shopping experience</li>
                    <li>• Remembering favorites and delivery preferences</li>
                    <li>
                      • Suggesting relevant health content, blogs, and guides
                    </li>
                  </ul>
                </div>
              </div>

              {/* 3.3 & 3.4: Marketing & Legal */}
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <h3 className="font-bold text-gray-900 border-b border-rose-100 pb-1">
                    3.3 Marketing & Communication
                  </h3>
                  <p className="text-sm text-gray-600 mb-2 italic">
                    You can opt-out anytime via "Manage Preferences".
                  </p>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Promotional emails (discounts and new arrivals)</li>
                    <li>• SMS alerts for renewals or order status</li>
                    <li>
                      • WhatsApp notifications (only with your permission)
                    </li>
                    <li>• Mobile app push notifications</li>
                  </ul>
                </div>
                <div className="space-y-3">
                  <h3 className="font-bold text-gray-900 border-b border-rose-100 pb-1">
                    3.4 Compliance & Legal
                  </h3>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Age verification for hygiene product safety</li>
                    <li>• Compliance with GST, tax, and financial laws</li>
                    <li>• Preventing fraud and unauthorized payments</li>
                    <li>• Responding to valid government or legal requests</li>
                    <li>
                      • Maintaining records for Consumer Disputes (
                      <span className="font-semibold text-rose-600">
                        6-year legal requirement
                      </span>
                      )
                    </li>
                  </ul>
                </div>
              </div>

              {/* 3.5, 3.6, 3.7: Optimization & Fraud */}
              <div className="bg-gray-50 p-6 rounded-2xl border border-gray-200">
                <div className="grid md:grid-cols-3 gap-6">
                  <div>
                    <h4 className="font-bold text-gray-800 text-sm mb-2 uppercase">
                      3.5 Analytics
                    </h4>
                    <ul className="text-xs text-gray-600 space-y-1">
                      <li>• Analyzing traffic and conversions</li>
                      <li>
                        • Improve website performance and user experience{" "}
                      </li>
                      <li>• A/B testing new site features</li>
                      <li>• Understand which products are popular </li>
                      <li>• Fixing technical/performance issues</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-800 text-sm mb-2 uppercase">
                      3.6 R&D
                    </h4>
                    <ul className="text-xs text-gray-600 space-y-1">
                      <li>• Understand customer needs and pain points </li>
                      <li>• Developing new product lines</li>
                      <li>• Improving packaging and delivery</li>
                      <li>• Anonymized market research surveys</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-800 text-sm mb-2 uppercase">
                      3.7 Fraud Prevention
                    </h4>
                    <ul className="text-xs text-gray-600 space-y-1">
                      <li>• Detecting suspicious chargebacks</li>
                      <li>• Identifying fraudulent accounts</li>
                      <li>• Verify payment legitimacy </li>
                      <li>• Preventing unauthorized reselling</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* 3.8: Health Data Protection - HIGH PRIORITY */}
              <div className="bg-rose-600 text-white p-6 rounded-2xl shadow-md">
                <div className="flex items-center gap-3 mb-4">
                  <ShieldCheck size={24} className="text-rose-200" />
                  <h3 className="font-bold text-xl">
                    3.8 Health Data - Special Protections
                  </h3>
                </div>
                <p className="text-sm text-rose-100 mb-4">
                  Your health and menstrual information is treated as{" "}
                  <strong>SENSITIVE</strong> under the DPDP Act. We use this
                  data <strong>ONLY</strong> for:
                </p>
                <ul className="grid md:grid-cols-2 gap-2 text-sm mb-6">
                  <li className="flex items-center gap-2">
                    <span className="text-rose-300">✓</span> Product matching
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-rose-300">✓</span> Personalized
                    wellness tips
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-rose-300">✓</span> Anonymized research
                    insights
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-rose-300">✓</span> Essential account
                    records
                  </li>
                </ul>

                <div className="bg-rose-700/50 p-4 rounded-xl border border-rose-400/30">
                  <p className="text-xs font-bold uppercase tracking-widest text-rose-200 mb-2">
                    We will NEVER:
                  </p>
                  <ul className="grid md:grid-cols-2 gap-x-6 gap-y-1 text-xs opacity-90">
                    <li>• Sell this data to any third party</li>
                    <li>• Share with insurance companies</li>
                    <li>
                      • Share with healthcare providers (unless requested)
                    </li>
                    <li>• Use for advertising without explicit consent</li>
                    <li>• Disclose to employers or family members</li>
                  </ul>
                </div>
              </div>

              {/* 3.9: Corporate/B2B */}
              <div className="border-l-4 border-gray-300 pl-6 py-2">
                <h3 className="font-bold text-gray-900 mb-2">
                  3.9 Corporate & B2B Subscriptions
                </h3>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li>
                    • <strong>Privacy First:</strong> Individual employee data
                    is <strong>never</strong> shared with employers.
                  </li>
                  <li>
                    • <strong>Aggregate Reporting:</strong> HR receives only
                    anonymized, high-level reports (e.g., total sustainability
                    trends).
                  </li>
                  <li>
                    • <strong>Compliance:</strong> Corporate clients are bound
                    by separate, strict privacy agreements to ensure employee
                    anonymity.
                  </li>
                </ul>
              </div>
            </div>
          </Section>

          {/* 4. Data Sharing */}
          <Section
            title="4. Data Sharing and Third Parties"
            icon={<Share2 size={28} className="text-rose-500" />}
          >
            {/* 4.1: The Pledge */}
            <div className="bg-emerald-50 border border-emerald-200 p-6 rounded-2xl mb-8 flex flex-col md:flex-row items-center gap-4">
              <div className="bg-emerald-500 text-white p-3 rounded-full">
                <ShieldCheck size={32} />
              </div>
              <div>
                <h3 className="text-emerald-900 font-bold text-xl">
                  4.1 We Do NOT Sell Your Data
                </h3>
                <p className="text-emerald-700 text-sm">
                  We do <strong>NOT</strong> sell your personal data to
                  advertisers or brokers for profit. Your privacy is our
                  priority, not a product. You maintain the right to opt-out of
                  data sharing as detailed in Section 6.
                </p>
              </div>
            </div>

            {/* 4.2: Service Providers */}
            <div className="mb-8">
              <h3 className="font-bold text-gray-900 text-lg mb-4">
                4.2 When We Share Data
              </h3>
              <p className="text-gray-700 mb-4">
                We only share data with <strong>Trusted Partners</strong> who
                help us operate our services. These partners are bound by strict
                confidentiality agreements:
              </p>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="p-4 border border-gray-100 rounded-xl bg-white shadow-sm">
                  <h4 className="font-bold text-rose-600 text-sm mb-2 italic">
                    A. Service Providers
                  </h4>
                  <ul className="text-xs text-gray-600 space-y-2">
                    <li>
                      <strong>Payments:</strong> PhonePe, Zoho, Razorpay
                      (Processing only)
                    </li>
                    <li>
                      <strong>Logistics:</strong> Courier partners (For delivery
                      tracking)
                    </li>
                    <li>
                      <strong>Communications:</strong> Mailchimp & SMS providers
                      (OTP/Updates)
                    </li>
                    <li>
                      <strong>Analytics:</strong> Google Analytics & Zoho
                      Analytics
                    </li>
                    <li>
                      <strong>Infrastructure:</strong> AWS & Google Cloud
                      Platform (Secure storage)
                    </li>
                    <li>
                      <strong>Medical:</strong> Pharmeasy & Thyrocare (Service
                      fulfillment)
                    </li>
                    <p>
                      These partners are bound by confidentiality agreements and
                      use data only for the service they provide (wherever
                      applicable).{" "}
                    </p>
                  </ul>
                </div>

                <div className="space-y-4">
                  <div className="p-4 border border-gray-100 rounded-xl bg-white shadow-sm">
                    <h4 className="font-bold text-gray-800 text-sm mb-1">
                      B. Business Transfers
                    </h4>
                    <p className="text-xs text-gray-600">
                      In a merger or acquisition, your data may transfer. We
                      will notify you and ensure the new owner honors this
                      policy.
                    </p>
                  </div>
                  <div className="p-4 border border-gray-100 rounded-xl bg-white shadow-sm">
                    <h4 className="font-bold text-gray-800 text-sm mb-1">
                      C. Legal Obligations
                    </h4>
                    <p className="text-xs text-gray-600">
                      Disclosures to Income Tax, RBI, Police, or Consumer
                      Commissions when legally required by court orders.
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-4 p-4 bg-rose-50 rounded-xl border border-rose-100">
                <h4 className="font-bold text-rose-800 text-sm mb-2">
                  D. With Your Explicit Consent
                </h4>
                <p className="text-xs text-gray-700 leading-relaxed">
                  We share data when you specifically ask us to: posting reviews
                  on social media, participating in anonymized research,
                  authorizing corporate wellness insights, or requesting data
                  sharing with your doctor/nutritionist.
                </p>
              </div>
            </div>

            {/* 4.3: Retention Timelines */}
            <div className="mb-8">
              <h3 className="font-bold text-gray-900 text-lg mb-4">
                4.3 Data Retention by Third Parties
              </h3>
              <div className="overflow-hidden rounded-xl border border-gray-200">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold text-gray-700">
                        Partner Type
                      </th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-700">
                        Retention Period
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white text-gray-600">
                    <tr>
                      <td className="px-4 py-3">Payment Gateways</td>
                      <td className="px-4 py-3">
                        7 Years (Banking Regulations)
                      </td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3">Courier Partners</td>
                      <td className="px-4 py-3">1 Year (Then deleted)</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3">Email Providers</td>
                      <td className="px-4 py-3">
                        Active Subscription + 90 Days
                      </td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3">Cloud Providers</td>
                      <td className="px-4 py-3">Per our Retention Schedule</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* 4.4: International Transfers */}
            <div className="bg-gray-900 text-white p-6 rounded-2xl">
              <div className="flex items-center gap-3 mb-3">
                <Globe size={24} className="text-rose-400" />
                <h3 className="font-bold text-lg">
                  4.4 International Data Transfer
                </h3>
              </div>
              <p className="text-sm text-gray-300 leading-relaxed">
                Our cloud servers (AWS/GCP) may be located in{" "}
                <strong>Singapore or US regions</strong>. Under the{" "}
                <strong>DPDP Act</strong>, we ensure these transfers maintain
                adequate safeguards, including end-to-end encryption and
                security standards equivalent to Indian law. By using our
                platform, you consent to this secure international transfer.
              </p>
            </div>
          </Section>

          {/* 5. Data Security */}
          <Section
            title="5. Data Security and How We Store Data"
            icon={<Lock size={28} className="text-rose-500" />}
          >
            {/* 5.1 Security Measures */}
            <h3 className="font-bold text-xl text-gray-900 mt-2 mb-4 border-b border-rose-100 pb-2">
              5.1 Security Measures
            </h3>
            <p className="text-gray-700 mb-4 text-lg">
              We implement industry-standard protections to safeguard your
              information:
            </p>
            <ul className="grid md:grid-cols-2 gap-4 mb-8">
              <li className="flex items-start gap-3 p-3 bg-white border border-gray-100 rounded-xl shadow-sm">
                <div className="bg-rose-100 p-2 rounded-lg text-rose-600">
                  <Shield size={18} />
                </div>
                <span className="text-sm text-gray-700">
                  <strong>Encryption:</strong> HTTPS/TLS (256-bit) in transit
                  and sensitive data encrypted at rest in our databases.
                </span>
              </li>
              <li className="flex items-start gap-3 p-3 bg-white border border-gray-100 rounded-xl shadow-sm">
                <div className="bg-rose-100 p-2 rounded-lg text-rose-600">
                  <Users size={18} />
                </div>
                <span className="text-sm text-gray-700">
                  <strong>Access Controls:</strong> Role-based permissions
                  ensure only authorized employees can access sensitive data.
                </span>
              </li>
              <li className="flex items-start gap-3 p-3 bg-white border border-gray-100 rounded-xl shadow-sm">
                <div className="bg-rose-100 p-2 rounded-lg text-rose-600">
                  <Globe size={18} />
                </div>
                <span className="text-sm text-gray-700">
                  <strong>Firewalls:</strong> Advanced network protection to
                  block unauthorized external access.
                </span>
              </li>
              <li className="flex items-start gap-3 p-3 bg-white border border-gray-100 rounded-xl shadow-sm">
                <div className="bg-rose-100 p-2 rounded-lg text-rose-600">
                  <Search size={18} />
                </div>
                <span className="text-sm text-gray-700">
                  <strong>Security Audits:</strong> Annual penetration testing
                  and vulnerability assessments.
                </span>
              </li>
              <li className="flex items-start gap-3 p-3 bg-white border border-gray-100 rounded-xl shadow-sm">
                <div className="bg-rose-100 p-2 rounded-lg text-rose-600">
                  <Key size={18} />
                </div>
                <span className="text-sm text-gray-700">
                  <strong>MFA:</strong> Multi-Factor Authentication required for
                  all administrative access.
                </span>
              </li>
              <li className="flex items-start gap-3 p-3 bg-white border border-gray-100 rounded-xl shadow-sm">
                <div className="bg-rose-100 p-2 rounded-lg text-rose-600">
                  <Database size={18} />
                </div>
                <span className="text-sm text-gray-700">
                  <strong>Secure Backups:</strong> Daily encrypted backups
                  stored in redundant, secure locations.
                </span>
              </li>
            </ul>

            <div className="bg-rose-50 border-l-4 border-rose-500 p-5 rounded-r-xl mb-8">
              <p className="text-sm text-rose-900 leading-relaxed">
                <strong>Incident Response Plan:</strong> In case of a data
                breach, we follow a strict protocol to notify affected users and
                the relevant authorities within <strong>72 hours</strong>, as
                per the <strong>DPDP Act</strong> requirements.
              </p>
            </div>

            {/* 5.2 Payment Card Data */}
            <div className="mb-8">
              <h3 className="font-bold text-lg text-gray-900 mb-3 flex items-center gap-2">
                <CreditCard size={22} className="text-rose-500" /> 5.2 Payment
                Card Data
              </h3>
              <div className="bg-gray-50 p-5 rounded-2xl border border-gray-200">
                <ul className="space-y-3 text-sm text-gray-700">
                  <li className="flex items-center gap-2">
                    <span className="text-rose-500 font-bold">•</span> We do{" "}
                    <strong>NOT</strong> store your full credit or debit card
                    numbers.
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-rose-500 font-bold">•</span>{" "}
                    <strong>Tokenization:</strong> Payment gateways (Zoho,
                    PhonePe, Razorpay) store encrypted card tokens per{" "}
                    <strong>PCI-DSS standards</strong>.
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-rose-500 font-bold">•</span> We only
                    store transaction confirmations and the last-4 digits for
                    your reference.
                  </li>
                </ul>
              </div>
            </div>

            {/* 5.3 Password Security */}
            <div className="mb-10">
              <h3 className="font-bold text-lg text-gray-900 mb-3 flex items-center gap-2">
                <Lock size={22} className="text-rose-500" /> 5.3 Password
                Security
              </h3>
              <div className="space-y-4 text-sm text-gray-700">
                <p>
                  Your password is hashed using{" "}
                  <strong>bcrypt (one-way encryption)</strong>. We never store
                  passwords in plain text, and we cannot retrieve them; if
                  forgotten, you must perform a reset.
                </p>
                <p className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 font-semibold italic">
                  Important: You are solely responsible for maintaining the
                  confidentiality of your password and account credentials.
                </p>
              </div>
            </div>

            {/* 5.4 Data Retention Schedule (CORRECTED) */}
            <h3 className="font-bold text-xl text-gray-900 mb-4 border-b border-rose-100 pb-2">
              5.4 Data Retention Schedule
            </h3>
            <div className="overflow-x-auto rounded-xl border border-rose-200 shadow-sm mb-10">
              <table className="w-full text-sm text-left border-collapse bg-white">
                <thead className="bg-rose-600 text-white">
                  <tr>
                    <th className="p-4 font-semibold whitespace-nowrap">
                      Data Type
                    </th>
                    <th className="p-4 font-semibold whitespace-nowrap">
                      Retention Period
                    </th>
                    <th className="p-4 font-semibold whitespace-nowrap">
                      Reason for Retention
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-rose-100">
                  <tr>
                    <td className="p-4 font-medium">
                      Account Info (Name, Email, etc.)
                    </td>
                    <td className="p-4">Until account deleted + 90 days</td>
                    <td className="p-4 text-gray-600 italic">
                      Dispute resolution
                    </td>
                  </tr>
                  <tr className="bg-rose-50/30">
                    <td className="p-4 font-medium">
                      Order & Transaction Records
                    </td>
                    <td className="p-4">6 Years</td>
                    <td className="p-4 text-gray-600 italic">
                      GST / Tax law requirement
                    </td>
                  </tr>
                  <tr>
                    <td className="p-4 font-medium">Payment Confirmations</td>
                    <td className="p-4">7 Years</td>
                    <td className="p-4 text-gray-600 italic">
                      RBI Banking regulations
                    </td>
                  </tr>
                  <tr className="bg-rose-50/30">
                    <td className="p-4 font-medium">
                      Health & Preference Data
                    </td>
                    <td className="p-4">2 years after last order</td>
                    <td className="p-4 text-gray-600 italic">
                      Recommendations & disputes
                    </td>
                  </tr>
                  <tr>
                    <td className="p-4 font-medium">Website Analytics</td>
                    <td className="p-4">26 Months</td>
                    <td className="p-4 text-gray-600 italic">
                      Google Analytics default
                    </td>
                  </tr>
                  <tr className="bg-rose-50/30">
                    <td className="p-4 font-medium">
                      Support Chat Conversations
                    </td>
                    <td className="p-4">1 Year</td>
                    <td className="p-4 text-gray-600 italic">
                      Customer service records
                    </td>
                  </tr>
                  <tr>
                    <td className="p-4 font-medium">
                      Marketing Emails (Unsubscribed)
                    </td>
                    <td className="p-4">3 Months</td>
                    <td className="p-4 text-gray-600 italic">
                      Compliance with NISM guidelines
                    </td>
                  </tr>
                  <tr className="bg-rose-50/30">
                    <td className="p-4 font-medium text-rose-700 font-bold">
                      Deleted Account Data
                    </td>
                    <td className="p-4">90 Days</td>
                    <td className="p-4 text-gray-600 italic">
                      Allow for recovery requests
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* 5.5 & 5.6 Data Minimization and Limitations */}
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="font-bold text-gray-900 mb-3 uppercase tracking-wide text-sm">
                  5.5 Data Minimization
                </h3>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-center gap-2">
                    <CheckCircle size={16} className="text-emerald-500" /> We
                    collect ONLY data necessary to serve you.
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle size={16} className="text-emerald-500" /> We do
                    NOT ask for information that is not required.
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle size={16} className="text-emerald-500" />{" "}
                    Annual privacy audits ensure zero over-collection.
                  </li>
                </ul>
              </div>
              <div className="bg-gray-900 text-white p-6 rounded-2xl">
                <h3 className="font-bold mb-3 flex items-center gap-2 text-rose-400">
                  <AlertTriangle size={20} /> 5.6 Security Limitations
                </h3>
                <ul className="space-y-3 text-xs text-gray-300 leading-relaxed">
                  <li>
                    • While we use industry-standard security, no system is 100%
                    secure.
                  </li>
                  <li>
                    • We are <strong>NOT</strong> liable for unauthorized access
                    due to account sharing, weak passwords, or your personal
                    device being compromised.
                  </li>
                  <li>
                    • <strong>Action:</strong> If you suspect a breach, change
                    your password immediately and contact support.
                  </li>
                </ul>
              </div>
            </div>
          </Section>

          {/* 6. Your Rights */}
          <Section
            title="6. Your Rights Under DPDP Act & Consumer Protection Act"
            icon={<Edit3 size={28} className="text-rose-500" />}
          >
            <div className="grid md:grid-cols-2 gap-6 mt-6">
              {/* 6.1 Right to Access */}
              <div className="bg-white p-5 rounded-xl border border-rose-100 shadow-sm">
                <h4 className="font-bold text-rose-600 mb-2 flex items-center gap-2">
                  <FileText size={18} /> 6.1 Right to Access
                </h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Request copy of all personal data held.</li>
                  <li>
                    • Provided in <b>CSV or PDF</b> within <b>30 days</b>.
                  </li>
                  <li>
                    • <b>Free</b> for 1st request/year; ₹500-1000 for
                    subsequent.
                  </li>
                  <li>• Right remains valid even after account closure.</li>
                </ul>
              </div>

              {/* 6.2 Right to Correction */}
              <div className="bg-white p-5 rounded-xl border border-rose-100 shadow-sm">
                <h4 className="font-bold text-rose-600 mb-2 flex items-center gap-2">
                  <Edit3 size={18} /> 6.2 Right to Correction
                </h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Fix inaccurate address, phone, or preferences.</li>
                  <li>
                    • Corrections completed within <b>7 days</b>.
                  </li>
                  <li>• Edit directly in "Account Settings" or notify us.</li>
                </ul>
              </div>

              {/* 6.3 Right to Deletion */}
              <div className="bg-white p-5 rounded-xl border border-rose-100 shadow-sm">
                <h4 className="font-bold text-rose-600 mb-2 flex items-center gap-2">
                  <AlertCircle size={18} /> 6.3 Right to Deletion
                </h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>
                    • "Right to be Forgotten" within <b>30 days</b>.
                  </li>
                  <li>
                    • <b>Exceptions:</b> GST records (6 yrs), Tax records (7
                    yrs).
                  </li>
                  <li>
                    • Deletion includes loss of purchase history & access.
                  </li>
                </ul>
              </div>

              {/* 6.4 & 6.5 Withdrawal & Restriction */}
              <div className="bg-white p-5 rounded-xl border border-rose-100 shadow-sm">
                <h4 className="font-bold text-rose-600 mb-2 flex items-center gap-2">
                  <ShieldCheck size={18} /> 6.4 & 6.5 Withdraw & Restrict
                </h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>
                    • Withdraw consent anytime (affects future processing only).
                  </li>
                  <li>
                    • Withdrawal may limit services (e.g., cannot process
                    orders).
                  </li>
                  <li>
                    • <b>Pause</b> specific data use; we honor within{" "}
                    <b>7 days</b>.
                  </li>
                </ul>
              </div>

              {/* 6.6 Data Portability */}
              <div className="bg-white p-5 rounded-xl border border-rose-100 shadow-sm">
                <h4 className="font-bold text-rose-600 mb-2 flex items-center gap-2">
                  <CheckCircle size={18} /> 6.6 Data Portability
                </h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>
                    • Data in <b>JSON or CSV</b> for porting to other services.
                  </li>
                  <li>
                    • Provided within <b>30 days at no cost</b>.
                  </li>
                </ul>
              </div>

              {/* 6.7 Marketing Opt-Out */}
              <div className="bg-white p-5 rounded-xl border border-rose-100 shadow-sm">
                <h4 className="font-bold text-rose-600 mb-2 flex items-center gap-2">
                  <Mail size={18} /> 6.7 Opt-Out of Marketing
                </h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Email "Unsubscribe" or SMS "STOP" to opt-out.</li>
                  <li>
                    • Marketing stops within <b>2 days</b>.
                  </li>
                  <li>• Push notifications can be disabled in app settings.</li>
                </ul>
              </div>
            </div>
 <div className="text-sm text-gray-600">
                <p className="font-bold text-gray-800 mb-1">
                  6.8 Right to Lodge Complaints
                </p>
                <p>
                  File with the <b>Data Protection Board of India (DPBI)</b> or
                  your local <b>District Consumer Commission</b>.
                </p>
              </div>
            {/* 6.9 Exercise Process */}
            <div className="mt-8 bg-rose-50 p-6 rounded-xl border border-rose-100">
              <h4 className="font-bold text-gray-800 mb-4 text-center">
                6.9 How to Exercise Your Rights
              </h4>
              <div className="grid md:grid-cols-3 gap-4 text-xs text-gray-700">
                <div className="bg-white p-3 rounded shadow-sm">
                  <span className="font-bold text-rose-600">Step 1:</span> Email{" "}
                  <b>support@eveya.in</b> with subject line like "Data Access
                  Request".
                </div>
                <div className="bg-white p-3 rounded shadow-sm">
                  <span className="font-bold text-rose-600">Step 2:</span>{" "}
                  Include Name, Email, Order ID, and specific data requested (ID
                  proof may be required).
                </div>
                <div className="bg-white p-3 rounded shadow-sm">
                  <span className="font-bold text-rose-600">Step 3:</span> We
                  respond in <b>30 days</b> (extendable by 15 days for complex
                  requests).
                </div>
              </div>
            </div>

            {/* 6.8 & 6.10 Complaints & Retaliation */}
            <div className="mt-6 space-y-4 text-center">
             

              <div className="inline-block px-4 py-2 bg-green-50 border border-green-200 rounded-full text-sm text-green-700 font-medium">
                6.10 <b>No Retaliation:</b> We will NOT penalize you for
                exercising your privacy rights.
              </div>
            </div>
          </Section>

          {/* 7. Cookies */}
          <Section
            title="7. Cookies, Tracking and Advertising"
            icon={<Cookie size={28} className="text-rose-500" />}
          >
            <div className="space-y-8">
              {/* 7.1 What Are Cookies? */}
              <div>
                <p className="text-gray-700 leading-relaxed">
                  Cookies are small text files stored on your device that help
                  our website remember your login, track usage patterns, show
                  personalized ads, and measure performance.
                </p>
              </div>

              {/* 7.2 Cookie Table */}
              <div className="overflow-x-auto rounded-xl border border-rose-100">
                <table className="w-full text-left text-sm">
                  <thead className="bg-rose-50 text-rose-700 font-bold">
                    <tr>
                      <th className="p-3">7.2 Cookie Type</th>
                      <th className="p-3">Purpose</th>
                      <th className="p-3">Duration</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-rose-50 text-gray-600">
                    <tr>
                      <td className="p-3 font-medium">Session Cookies</td>
                      <td className="p-3">Keep you logged in</td>
                      <td className="p-3">Until logout</td>
                    </tr>
                    <tr>
                      <td className="p-3 font-medium">Preference Cookies</td>
                      <td className="p-3">
                        Remember settings (language, theme)
                      </td>
                      <td className="p-3">1 year</td>
                    </tr>
                    <tr>
                      <td className="p-3 font-medium">Analytics Cookies</td>
                      <td className="p-3">
                        Track traffic, page views, conversions
                      </td>
                      <td className="p-3">2 years</td>
                    </tr>
                    <tr>
                      <td className="p-3 font-medium">Marketing Cookies</td>
                      <td className="p-3">Show personalized product ads</td>
                      <td className="p-3">1 year</td>
                    </tr>
                    <tr>
                      <td className="p-3 font-medium">Security Cookies</td>
                      <td className="p-3">Protect against fraud and attacks</td>
                      <td className="p-3">Session</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* 7.3 & 7.7 Third-Party Advertising & Retargeting */}
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white p-5 rounded-xl border border-rose-100 shadow-sm">
                  <h4 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
                    <MousePointer2 size={18} className="text-rose-500" /> 7.3 &
                    7.7 Ad Retargeting
                  </h4>
                  <p className="text-xs text-gray-600 mb-4">
                    If you visit but don't purchase, you may see our ads on
                    Facebook, Instagram, and Google Search. We use{" "}
                    <b>Facebook Pixel</b> and <b>Google Ads</b> to remind you of
                    products you viewed.
                  </p>
                  <div className="space-y-2">
                    <a
                      href="https://tools.google.com/dlpage/gaoptout"
                      target="_blank"
                      className="text-xs text-rose-600 hover:underline flex items-center gap-1"
                    >
                      <ExternalLink size={12} /> Opt-out of Google Tracking
                    </a>
                    <a
                      href="https://facebook.com/ads/preferences"
                      target="_blank"
                      className="text-xs text-rose-600 hover:underline flex items-center gap-1"
                    >
                      <ExternalLink size={12} /> Opt-out of Facebook Ads
                    </a>
                  </div>
                </div>

                {/* 7.4 Cookie Control */}
                <div className="bg-white p-5 rounded-xl border border-rose-100 shadow-sm">
                  <h4 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
                    <ShieldCheck size={18} className="text-rose-500" /> 7.4
                    Cookie Control
                  </h4>
                  <ul className="text-xs text-gray-600 space-y-1 mb-3">
                    <li>
                      • <b>Desktop:</b> Settings &gt; Privacy &gt; Cookies
                    </li>
                    <li>
                      • <b>Mobile:</b> Browser Settings &gt; Cookies
                    </li>
                    <li>
                      • <b>Tools:</b> Use extensions like Ghostery
                    </li>
                  </ul>
                  <div className="flex gap-4 text-xs">
                    <span className="text-green-600 font-medium">
                      ✓ Improves Privacy
                    </span>
                    <span className="text-red-600 font-medium">
                      ✗ Breaks Login/Cart
                    </span>
                  </div>
                </div>
              </div>

              {/* 7.5, 7.6, 7.8 Tracking Details */}
              <div className="grid md:grid-cols-3 gap-4">
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-100">
                  <h5 className="font-bold text-sm text-gray-800 mb-2 flex items-center gap-1">
                    <Info size={14} /> 7.5 DNT
                  </h5>
                  <p className="text-xs text-gray-600">
                    We honor "Do Not Track" signals for advertising cookies
                    only. Analytics/Essential cookies remain active.
                  </p>
                </div>

                <div className="p-4 bg-gray-50 rounded-lg border border-gray-100">
                  <h5 className="font-bold text-sm text-gray-800 mb-2 flex items-center gap-1">
                    <Eye size={14} /> 7.6 Pixel Tracking
                  </h5>
                  <p className="text-xs text-gray-600">
                    Emails contain invisible pixels to track opens/clicks.
                    Disable "images" in your email client to block this.
                  </p>
                </div>

                <div className="p-4 bg-gray-50 rounded-lg border border-gray-100">
                  <h5 className="font-bold text-sm text-gray-800 mb-2 flex items-center gap-1">
                    <Mail size={14} /> 7.8 Email Tracking
                  </h5>
                  <p className="text-xs text-gray-600">
                    We track effectiveness via clicks. Once you unsubscribe, all
                    tracking stops immediately.
                  </p>
                </div>
              </div>
            </div>
          </Section>
          {/* 8 & 9 */}
          <Section
            title="8. Children’s Privacy"
            icon={<UserMinus size={28} className="text-rose-500" />}
          >
            <div className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                {/* 8.1 Age Requirement */}
                <div className="bg-white p-5 rounded-xl border border-rose-100 shadow-sm">
                  <h4 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
                    <ShieldAlert size={18} className="text-rose-600" /> 8.1 Age
                    Requirement
                  </h4>
                  <ul className="text-sm text-gray-600 space-y-2">
                    <li>
                      • Services are strictly for users <b>18+ only</b>.
                    </li>
                    <li>
                      • We do <b>NOT</b> knowingly collect data from persons
                      under 18.
                    </li>
                    <li>
                      • Parents/guardians are prohibited from creating accounts
                      for minors.
                    </li>
                  </ul>
                </div>

                {/* 8.2 Discovery & Deletion */}
                <div className="bg-white p-5 rounded-xl border border-rose-100 shadow-sm">
                  <h4 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
                    <AlertTriangle size={18} className="text-amber-500" /> 8.2
                    Data Discovery
                  </h4>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    If we learn that a minor’s data has been collected, we will{" "}
                    <b>
                      delete the account and all associated data immediately
                    </b>{" "}
                    without retention. Contact us at{" "}
                    <a
                      href="mailto:support@eveya.in"
                      className="text-rose-600 underline"
                    >
                      support@eveya.in
                    </a>{" "}
                    if you believe this has occurred.
                  </p>
                </div>
              </div>

              {/* 8.3 Parental Consent */}
              <div className="bg-rose-50 p-4 rounded-xl border border-rose-100 flex items-start gap-4">
                <Users className="text-rose-500 mt-1 flex-shrink-0" size={20} />
                <div>
                  <h4 className="font-bold text-gray-800 text-sm">
                    8.3 Parental & Shared Accounts
                  </h4>
                  <p className="text-sm text-gray-600 mt-1">
                    Parents managing a young adult's (18+) account must have
                    explicit authorization. We recommend individual accounts
                    over shared ones, as shared access may compromise personal
                    privacy.
                  </p>
                </div>
              </div>
            </div>
          </Section>

          {/* Section 9: Changes to this Policy */}
          <Section
            title="9. Changes to Privacy Policies"
            icon={<RefreshCw size={28} className="text-rose-500" />}
          >
            <div className="space-y-6">
              {/* 9.1 Updates */}
              <div className="bg-white p-5 rounded-xl border border-rose-100 shadow-sm">
                <h4 className="font-bold text-gray-800 mb-2 flex items-center gap-2">
                  <Info size={18} className="text-rose-500" /> 9.1 Policy
                  Updates
                </h4>
                <p className="text-sm text-gray-600 leading-relaxed">
                  We may update this policy as laws change or our business
                  practices evolve. The most current version will always be
                  posted on our website with a
                  <span className="font-semibold text-rose-600">
                    {" "}
                    "Last Updated"{" "}
                  </span>{" "}
                  date clearly visible.
                </p>
              </div>

              {/* 9.2 Notification Logic */}
              <div className="grid md:grid-cols-2 gap-5">
                <div className="bg-rose-50 p-5 rounded-xl border border-rose-100">
                  <h4 className="font-bold text-gray-800 mb-3 flex items-center gap-2 text-sm">
                    <Bell size={16} className="text-rose-600" /> 9.2
                    Notification Rules
                  </h4>
                  <ul className="text-xs text-gray-700 space-y-3">
                    <li className="flex gap-2">
                      <CheckCircle2
                        size={14}
                        className="text-green-600 flex-shrink-0"
                      />
                      <span>
                        <b>Material Changes:</b> (e.g., new data sharing) will
                        be notified via email.
                      </span>
                    </li>
                    <li className="flex gap-2">
                      <XCircle
                        size={14}
                        className="text-gray-400 flex-shrink-0"
                      />
                      <span>
                        <b>Non-Material:</b> (e.g., typos) will not be
                        explicitly notified.
                      </span>
                    </li>
                    <li className="flex gap-2">
                      <Calendar
                        size={14}
                        className="text-rose-600 flex-shrink-0"
                      />
                      <span>
                        <b>Subscribers:</b> Will receive <b>30 days' notice</b>{" "}
                        for any material changes.
                      </span>
                    </li>
                  </ul>
                </div>

                {/* 9.3 Your Options */}
                <div className="bg-white p-5 rounded-xl border border-rose-100 shadow-sm">
                  <h4 className="font-bold text-gray-800 mb-3 flex items-center gap-2 text-sm">
                    <XCircle size={16} className="text-rose-600" /> 9.3 Your
                    Options
                  </h4>
                  <p className="text-xs text-gray-600 mb-3">
                    If you disagree with the updated policy, you have the right
                    to:
                  </p>
                  <div className="bg-gray-50 p-3 rounded border border-gray-100 text-xs font-medium text-gray-700 mb-3">
                    Request account and data deletion (See Section 6.3)
                  </div>
                  <p className="text-[10px] text-gray-500 italic">
                    Note: Continued use of the site after updates constitute
                    your full consent to the new terms.
                  </p>
                </div>
              </div>
            </div>
          </Section>
        </div>

        {/* Footer */}
        {/* <footer className="mt-24 text-center text-gray-600 text-sm pb-12">
          <p className="font-medium">© {new Date().getFullYear()} Farmfit Agrihealth LLP. All rights reserved.</p>
          <p className="mt-4 uppercase tracking-widest font-bold text-xs text-rose-600">
            Eveya – Wellness. Privacy. Transparency.
          </p>
        </footer> */}
      </div>
    </div>
  );
};

const Section = ({ title, icon, children }) => (
  <section className="bg-white rounded-3xl p-9 border border-rose-100 shadow-xl shadow-rose-200/40 hover:shadow-rose-300/50 transition-all duration-300">
    <div className="flex items-center gap-5 mb-7">
      {icon && (
        <div className="p-3.5 bg-gradient-to-br from-rose-50 to-rose-100 rounded-2xl shadow-sm">
          {icon}
        </div>
      )}
      <h2 className="text-3xl font-bold text-gray-900 tracking-tight">
        {title}
      </h2>
    </div>
    <div className="space-y-5 text-gray-700 leading-relaxed text-[15.5px]">
      {children}
    </div>
  </section>
);

export default PrivacyPolicy;
